import {
  motion,
} from "framer-motion";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  getLanguageFromPath,
  getRoutePath,
} from "../site/config/routes";

import aboutData from "../site/data/about";

import Footer from "../template/components/layout/Footer";
import HandwrittenEyebrow from "../template/components/common/HandwrittenEyebrow";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  ABOUT PAGE
  ============================================================

  Varsinainen kuvaajasivu.

  Sisältö tulee about.js:stä WordPress-tyylisessä muodossa.
  React vastaa layoutista, animaatioista ja käytöksestä.
*/

export default function AboutPage() {
  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  const localized =
    (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      if (
        typeof value ===
        "string"
      ) {
        return value;
      }

      return (
        value[language] ??
        value.fi ??
        value.en ??
        ""
      );
    };

  const page =
    aboutData.page;

  return (
    <>
      {/* ======================================================
          HERO
          ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-b
          border-border
          bg-canvas
        "
      >
        <motion.div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            z-0
            w-[58%]
            overflow-hidden

            max-sm:right-[-35%]
            max-sm:w-full
          "
          initial={{
            opacity: 0,
            scale: 1.035,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1.5,
            delay: 0.15,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
          aria-hidden="true"
        >
          <img
            src="/media/heroes/about-hero.webp"
            alt=""
            loading="eager"
            decoding="async"
            className="
              h-full
              w-full
              object-cover
              opacity-[0.27]
              grayscale
              saturate-[0.7]
              contrast-[0.9]
            "
            style={{
              objectPosition:
                "35% 50%",
            }}
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-canvas
              via-canvas/55
              to-transparent
            "
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-b
              from-canvas/10
              via-transparent
              to-canvas/40
            "
          />
        </motion.div>

        <div
          className="
            relative
            z-10
            mx-auto
            grid
            min-h-[72svh]
            w-full
            max-w-editorial
            items-end
            gap-12
            px-6
            pb-16
            pt-36

            sm:px-10
            sm:pb-20
            sm:pt-44

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.34fr)]
            lg:gap-20
            lg:px-16
            lg:pb-24
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 28,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <HandwrittenEyebrow
              label={localized(
                page.hero.eyebrow
              )}
            />

            <h1
              className="
                m-0
                max-w-5xl
                min-w-0
                break-words
                font-display
                text-[clamp(4rem,10vw,9.5rem)]
                font-normal
                leading-[0.84]
                tracking-[-0.055em]
                text-text

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.hero.title
              )}
            </h1>
          </motion.div>

          <motion.div
            className="
              max-w-md
              min-w-0
            "
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.85,
              delay: 0.16,
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                page.hero.description
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          PORTRAIT / INTRODUCTION
          ====================================================== */}

      <section
        className="
          border-b
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-14
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-12
            lg:items-center
            lg:gap-x-16
            lg:px-16
            lg:py-40
          "
        >
          <motion.div
            className="
              min-w-0

              lg:col-span-7
            "
            initial={{
              opacity: 0,
              y: 34,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.95,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                relative
                aspect-[4/5]
                overflow-hidden
                bg-canvas

                sm:aspect-[5/6]
              "
            >
              <img
                src={
                  aboutData.image
                }
                alt={localized(
                  page.portrait.alt
                )}
                className="
                  h-full
                  w-full
                  object-cover
                  transition-transform
                  duration-[1200ms]
                  ease-out

                  hover:scale-[1.012]
                "
                style={{
                  objectPosition:
                    aboutData.imagePosition,
                }}
              />
            </div>

            <div
              className="
                mt-5
                flex
                items-center
                justify-between
                gap-6
              "
            >
              <p
                className="
                  m-0
                  text-[9px]
                  uppercase
                  tracking-[0.22em]
                  text-muted

                  sm:text-[10px]
                "
              >
                {localized(
                  page.portrait.caption
                )}
              </p>

              <span
                className="
                  font-display
                  text-3xl
                  italic
                  text-muted

                  sm:text-4xl
                "
                aria-hidden="true"
              >
                01
              </span>
            </div>
          </motion.div>

          <motion.div
            className="
              min-w-0

              lg:col-span-4
              lg:col-start-9
            "
            initial={{
              opacity: 0,
              y: 26,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.22,
            }}
            transition={{
              duration: 0.9,
              delay: 0.08,
            }}
          >
            <p
              className="
                mb-6
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                text-muted

                sm:text-[10px]
              "
            >
              {localized(
                page.portrait.eyebrow
              )}
            </p>

            <h2
              className="
                m-0
                max-w-full
                min-w-0
                break-words
                font-display
                text-[clamp(3rem,5vw,5.6rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.05em]
                text-text

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.portrait.title
              )}
            </h2>

            <div
              className="
                my-8
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-base
                leading-8
                text-muted
              "
            >
              {localized(
                page.portrait.text
              )}
            </p>

            <p
              className="
                mb-0
                mt-6
                text-base
                leading-8
                text-muted
              "
            >
              {localized(
                page.portrait.textTwo
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          PHILOSOPHY
          ====================================================== */}

      <section
        className="
          border-b
          border-border
          bg-canvas
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-12
            lg:px-16
            lg:py-44
          "
        >
          <div
            className="
              lg:col-span-3
            "
          >
            <p
              className="
                m-0
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                text-muted

                sm:text-[10px]
              "
            >
              02 /{" "}
              {localized(
                page.philosophy.eyebrow
              )}
            </p>
          </div>

          <motion.div
            className="
              min-w-0

              lg:col-span-8
              lg:col-start-5
            "
            initial={{
              opacity: 0,
              y: 28,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 0.9,
            }}
          >
            <h2
              className="
                m-0
                max-w-5xl
                break-words
                font-display
                text-[clamp(3.1rem,6.5vw,7rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.05em]
                text-text

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.philosophy.title
              )}
            </h2>

            <p
              className="
                mb-0
                mt-10
                max-w-2xl
                text-base
                leading-8
                text-muted

                sm:text-lg
                sm:leading-9
              "
            >
              {localized(
                page.philosophy.text
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          PRINCIPLES
          ====================================================== */}

      <section
        className="
          border-b
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-editorial
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:px-16
            lg:py-40
          "
        >
          <p
            className="
              mb-14
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:mb-20
              sm:text-[10px]
            "
          >
            03 /{" "}
            {localized(
              page.principles.eyebrow
            )}
          </p>

          <div
            className="
              grid
              border-t
              border-border

              lg:grid-cols-3
            "
          >
            {page.principles.items.map(
              (
                item,
                index
              ) => (
                <motion.article
                  key={item.id}
                  className="
                    min-w-0
                    border-b
                    border-border
                    py-10

                    lg:border-b-0
                    lg:border-r
                    lg:px-9
                    lg:py-14

                    first:lg:pl-0
                    last:lg:border-r-0
                    last:lg:pr-0
                  "
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                    amount: 0.3,
                  }}
                  transition={{
                    duration: 0.7,
                    delay:
                      index * 0.08,
                  }}
                >
                  <span
                    className="
                      text-[9px]
                      uppercase
                      tracking-[0.22em]
                      text-muted
                    "
                  >
                    {item.id}
                  </span>

                  <h3
                    className="
                      mb-0
                      mt-10
                      font-display
                      text-4xl
                      font-normal
                      tracking-[-0.04em]
                      text-text

                      sm:text-5xl
                    "
                  >
                    {localized(
                      item.title
                    )}
                  </h3>

                  <p
                    className="
                      mb-0
                      mt-7
                      max-w-sm
                      text-sm
                      leading-7
                      text-muted
                    "
                  >
                    {localized(
                      item.text
                    )}
                  </p>
                </motion.article>
              )
            )}
          </div>
        </div>
      </section>

      {/* ======================================================
          STATEMENT
          ====================================================== */}

      <section
        className="
          bg-canvas
        "
      >
        <div
          className="
            mx-auto
            w-full
            max-w-editorial
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:px-16
            lg:py-44
          "
        >
          <motion.div
            className="
              ml-auto
              max-w-5xl
            "
            initial={{
              opacity: 0,
              y: 28,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.25,
            }}
            transition={{
              duration: 0.9,
            }}
          >
            <p
              className="
                mb-7
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                text-muted

                sm:text-[10px]
              "
            >
              04 /{" "}
              {localized(
                page.statement.eyebrow
              )}
            </p>

            <h2
              className="
                m-0
                min-w-0
                break-words
                font-display
                text-[clamp(3.4rem,7vw,7.5rem)]
                font-normal
                leading-[0.88]
                tracking-[-0.05em]
                text-text

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.statement.title
              )}
            </h2>

            <p
              className="
                mb-0
                mt-10
                max-w-2xl
                text-base
                leading-8
                text-muted

                sm:text-lg
                sm:leading-9
              "
            >
              {localized(
                page.statement.text
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          CTA
          ====================================================== */}

      <section
        className="
          bg-text
          text-canvas
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.32fr)]
            lg:items-end
            lg:gap-20
            lg:px-16
            lg:py-40
          "
        >
          <div
            className="
              min-w-0
            "
          >
            <p
              className="
                mb-7
                text-[9px]
                font-medium
                uppercase
                tracking-[0.28em]
                opacity-60

                sm:text-[10px]
              "
            >
              {localized(
                page.cta.eyebrow
              )}
            </p>

            <h2
              className="
                m-0
                max-w-5xl
                break-words
                font-display
                text-[clamp(3.3rem,7vw,7.5rem)]
                font-normal
                leading-[0.88]
                tracking-[-0.05em]

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.cta.title
              )}
            </h2>
          </div>

          <div>
            <p
              className="
                m-0
                text-sm
                leading-7
                opacity-70

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                page.cta.text
              )}
            </p>

            <Link
              to={getRoutePath(
                "contact",
                language
              )}
              className="
                group
                mt-9
                inline-flex
                items-center
                gap-4
                border-b
                border-current
                pb-2
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.2em]
                text-current
                no-underline

                sm:text-[10px]
              "
            >
              {localized(
                page.cta.button
              )}

              <span
                className="
                  transition-transform
                  duration-300
                  group-hover:translate-x-1
                "
                aria-hidden="true"
              >
                →
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer
        language={language}
      />
    </>
  );
}
